import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { Pagination } from '../../components/ui/Pagination'
import { useClientPagination } from '../../hooks/useClientPagination'
import { BlocMementouri, type Memento } from '../../components/orgJuridic/BlocMementouri'

type Status = 'planned' | 'done' | 'cancelled'
type Type = 'intern' | 'client' | 'furnizor' | 'autoritate' | 'altele'

interface Participant {
  name: string
  role?: string | null
  email?: string | null
}

interface Meeting {
  id: number
  title: string
  date: string
  time: string | null
  location: string | null
  type: Type
  partnerType: string | null
  participants: Participant[]
  agenda: string | null
  notes: string | null
  meetingLink: string | null
  docLink: string | null
  status: Status
  /** Utilizatorii din CRM care primesc memento-urile. Separat de `participants`, care e text liber. */
  destinatari?: { user: { id: number; name: string | null; email: string } }[]
  mementouri?: Memento[]
}

const TYPE_LABELS: Record<Type, string> = {
  intern: 'Intern',
  client: 'Client',
  furnizor: 'Furnizor',
  autoritate: 'Autoritate',
  altele: 'Altele',
}
const STATUS_LABELS: Record<Status, string> = {
  planned: 'Planificată',
  done: 'Realizată',
  cancelled: 'Anulată',
}
const STATUS_COLORS: Record<Status, string> = {
  planned: 'bg-b4l-bg text-b4l-text-muted',
  done: 'bg-b4l-green-light text-b4l-green-dark',
  cancelled: 'bg-b4l-red-light text-b4l-red-dark',
}

export function IntalniriPage() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('meetings:write')

  const [createOpen, setCreateOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<Meeting | null>(null)

  const query = useQuery<{ meetings: Meeting[] }>({
    queryKey: ['meetings'],
    queryFn: async () => (await api.get<{ meetings: Meeting[] }>('/meetings')).data,
  })

  const createMutation = useMutation({
    mutationFn: async (data: Partial<Meeting>) => (await api.post('/meetings', data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meetings'] })
      toast.success('Întâlnire adăugată')
      setCreateOpen(false)
    },
    onError: showError,
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<Meeting> }) =>
      (await api.patch(`/meetings/${id}`, data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meetings'] })
      toast.success('Salvat')
      setEditTarget(null)
    },
    onError: showError,
  })

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: Status }) =>
      (await api.patch(`/meetings/${id}`, { status })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['meetings'] }),
    onError: showError,
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/meetings/${id}`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meetings'] })
      toast.success('Șters')
    },
  })

  const meetings = useMemo(() => query.data?.meetings ?? [], [query.data])
  const pag = useClientPagination(meetings, 10)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Întâlniri</h1>
        {canWrite && <Button onClick={() => setCreateOpen(true)}>+ Întâlnire nouă</Button>}
      </div>

      <Card description="Calendar întâlniri (clienți, furnizori, autorități, internă). Agendă, participanți, follow-ups, link Meet/Zoom și document.">
        {query.isLoading ? (
          <p className="text-sm text-b4l-text-muted">Se încarcă...</p>
        ) : meetings.length === 0 ? (
          <div className="rounded border border-dashed border-b4l-border p-8 text-center text-sm text-b4l-text-muted">
            Nicio întâlnire. {canWrite && 'Apasă „+ Întâlnire nouă”.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                  <th className="py-2 pr-3 font-medium">Data</th>
                  <th className="py-2 pr-3 font-medium">Titlu</th>
                  <th className="py-2 pr-3 font-medium">Tip</th>
                  <th className="py-2 pr-3 font-medium">Locație</th>
                  <th className="py-2 pr-3 font-medium">Participanți</th>
                  <th className="py-2 pr-3 font-medium">Stare</th>
                  {canWrite && <th className="py-2 pr-3 font-medium">Acțiuni</th>}
                </tr>
              </thead>
              <tbody>
                {pag.paginated.map((m) => (
                  <tr key={m.id} className="border-b border-b4l-border last:border-0">
                    <td className="py-2 pr-3 text-xs whitespace-nowrap">
                      {new Date(m.date).toLocaleDateString('en-GB')}
                      {m.time && <span className="ml-1 text-b4l-text-muted">{m.time}</span>}
                    </td>
                    <td className="py-2 pr-3 max-w-[260px] truncate" title={m.title}>
                      {m.title}
                      {m.meetingLink && (
                        <a
                          href={m.meetingLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2 text-b4l-blue-dark text-xs underline"
                        >
                          link ↗
                        </a>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-xs">{TYPE_LABELS[m.type]}</td>
                    <td className="py-2 pr-3 text-xs">{m.location ?? '—'}</td>
                    {/* Colegii alesi SI numele scrise de mana: coloana spune cine vine, de oriunde ar fi. */}
                    <td className="py-2 pr-3 text-xs">
                      {[
                        ...(m.destinatari ?? []).map((d) => d.user.name ?? d.user.email),
                        ...m.participants.map((p) => p.name),
                      ].join(', ') || '—'}
                    </td>
                    <td className="py-2 pr-3">
                      {canWrite ? (
                        <select
                          value={m.status}
                          onChange={(e) =>
                            statusMutation.mutate({ id: m.id, status: e.target.value as Status })
                          }
                          className={`rounded px-2 py-0.5 text-[11px] ${STATUS_COLORS[m.status]}`}
                        >
                          {(Object.keys(STATUS_LABELS) as Status[]).map((s) => (
                            <option key={s} value={s}>
                              {STATUS_LABELS[s]}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span
                          className={`rounded px-2 py-0.5 text-[11px] ${STATUS_COLORS[m.status]}`}
                        >
                          {STATUS_LABELS[m.status]}
                        </span>
                      )}
                    </td>
                    {canWrite && (
                      <td className="py-2 pr-3">
                        <div className="flex gap-1">
                          <Button
                            variant="secondary"
                            onClick={() => setEditTarget(m)}
                            className="!px-2 !py-1 !text-xs"
                          >
                            Editează
                          </Button>
                          <Button
                            variant="danger"
                            onClick={() => {
                              if (confirm(`Ștergi „${m.title}”?`)) deleteMutation.mutate(m.id)
                            }}
                            className="!px-2 !py-1 !text-xs"
                          >
                            Șterge
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-3">
          <Pagination
            page={pag.page}
            pageSize={pag.pageSize}
            total={pag.total}
            onPageChange={pag.setPage}
            onPageSizeChange={pag.setPageSize}
            itemLabel="întâlniri"
          />
        </div>
      </Card>

      {createOpen && (
        <MeetingFormModal
          title="Întâlnire nouă"
          submitLabel="Adaugă"
          onClose={() => setCreateOpen(false)}
          onSubmit={(d) => createMutation.mutate(d)}
          submitting={createMutation.isPending}
        />
      )}
      {editTarget && (
        <MeetingFormModal
          title={`Editează ${editTarget.title}`}
          submitLabel="Salvează"
          initial={editTarget}
          onClose={() => setEditTarget(null)}
          onSubmit={(d) => updateMutation.mutate({ id: editTarget.id, data: d })}
          submitting={updateMutation.isPending}
        />
      )}
    </div>
  )
}

function MeetingFormModal({
  title,
  submitLabel,
  initial,
  onClose,
  onSubmit,
  submitting,
}: {
  title: string
  submitLabel: string
  initial?: Meeting
  onClose: () => void
  onSubmit: (d: Partial<Meeting>) => void
  submitting: boolean
}) {
  const [t, setT] = useState(initial?.title ?? '')
  const [date, setDate] = useState(initial?.date ? initial.date.slice(0, 10) : '')
  const [time, setTime] = useState(initial?.time ?? '')
  const [location, setLocation] = useState(initial?.location ?? '')
  const [type, setType] = useState<Type>(initial?.type ?? 'client')
  const [participantsRaw, setParticipantsRaw] = useState(
    initial?.participants?.map((p) => p.name).join(', ') ?? '',
  )
  const [agenda, setAgenda] = useState(initial?.agenda ?? '')
  const [notes, setNotes] = useState(initial?.notes ?? '')
  const [meetingLink, setMeetingLink] = useState(initial?.meetingLink ?? '')
  const [docLink, setDocLink] = useState(initial?.docLink ?? '')
  const [destinatari, setDestinatari] = useState<number[]>(
    initial?.destinatari?.map((d) => d.user.id) ?? [],
  )
  const [mementouri, setMementouri] = useState<Memento[]>(
    initial?.mementouri?.map((m) => ({
      minuteInainte: m.minuteInainte,
      canal: m.canal,
      trimisLa: m.trimisLa,
    })) ?? [],
  )

  const submit = () => {
    if (!t.trim() || !date) {
      toast.error('Titlu și dată sunt obligatorii')
      return
    }
    const participants: Participant[] = participantsRaw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((name) => ({ name }))
    onSubmit({
      title: t.trim(),
      date: new Date(date).toISOString(),
      time: time.trim() || null,
      location: location.trim() || null,
      type,
      participants,
      agenda: agenda.trim() || null,
      notes: notes.trim() || null,
      meetingLink: meetingLink.trim() || null,
      docLink: docLink.trim() || null,
      destinatariIds: destinatari,
      mementouri: mementouri.map((m) => ({ minuteInainte: m.minuteInainte, canal: m.canal })),
    } as unknown as Partial<Meeting>)
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={title}
      /*
        Un clic alaturi nu mai arunca formularul. Alexandru, 01.10.2026: „daca dau click in afara
        ferestrei dispare, si nu se salveaza modificarile". Pe un formular cu agenda, note si
        memento-uri, inchiderea din greseala costa tot ce-ai scris — exact motivul pentru care
        formularul de necesar o oprise deja.
      */
      inchideLaClicInAfara={false}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Anulează
          </Button>
          <Button onClick={submit} loading={submitting}>
            {submitLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Titlu" value={t} onChange={(e) => setT(e.target.value)} />
        <div className="grid grid-cols-3 gap-3">
          <Field label="Data" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          <Field
            label="Ora (opțional)"
            placeholder="14:30"
            value={time}
            onChange={(e) => setTime(e.target.value)}
          />
          <div>
            <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Tip</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as Type)}
              className="w-full rounded border border-b4l-border px-3 py-2 text-sm"
            >
              {(Object.keys(TYPE_LABELS) as Type[]).map((x) => (
                <option key={x} value={x}>
                  {TYPE_LABELS[x]}
                </option>
              ))}
            </select>
          </div>
        </div>
        <Field
          label="Locație / sală"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
        />
        {/*
          „Alți participanți", nu „Participanți": colegii se aleg din lista de mai jos, in blocul
          de memento-uri. Doua campuri care se cheama amandoua „Participanti" erau o confuzie pe
          care am creat-o cand am adaugat selectorul — Alexandru a intrebat direct „cum adaug
          participantii? ma asteptam sa selectez de undeva".
        */}
        <Field
          label="Alți participanți, din afara firmei (separați prin virgulă)"
          placeholder="Diana Popescu, farm. Anita"
          value={participantsRaw}
          onChange={(e) => setParticipantsRaw(e.target.value)}
        />
        <div>
          <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Agendă</label>
          <textarea
            value={agenda}
            onChange={(e) => setAgenda(e.target.value)}
            rows={3}
            className="w-full rounded border border-b4l-border px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Note</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="w-full rounded border border-b4l-border px-3 py-2 text-sm"
          />
        </div>
        <BlocMementouri
          areOra={Boolean(time.trim())}
          destinatari={destinatari}
          setDestinatari={setDestinatari}
          mementouri={mementouri}
          setMementouri={setMementouri}
        />

        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Link Meet/Zoom"
            value={meetingLink}
            onChange={(e) => setMeetingLink(e.target.value)}
          />
          <Field
            label="Link document"
            value={docLink}
            onChange={(e) => setDocLink(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  )
}

function showError(err: unknown) {
  const message =
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? 'Eroare'
  toast.error(message)
}
